import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DatepickerHelper } from '../../../shared/validators/date-picker-form-validators';
import { enumToSelectOptions } from '../../../shared/utils/enums-to-list-formatter';
import { Company } from '@models/companies/company';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { CompanyChangeData } from '@models/ui-models/company-change-data';
import { SelectOption } from '@models/ui-models/select-options';
import { ChangeDictionaryHelper } from '@models/ui-models/change-dictionary-helper.model';
import { CompanyDividendStatementComponent } from '../../asic-forms/company';

export enum DividendStatementStepsEnum {
  FormDescription = 0,
  DistributionDetails = 1,
  DistributionAmount = 2,
  BeneficialOwnersDetails = 3,
}

export enum DividendType {
  Final = 0,
  Interim = 1
}

export enum PaymentType {
  Cash = 0,
  LoanAccountCredit = 1
}

export const paymentTypeOptions = enumToSelectOptions(PaymentType);

export const taxRateForFrankingPurposes: SelectOption[] = [0.25, 0.26, 0.275, 0.285, 0.30]
  .map((value) => ({
    label: `${ Math.ceil(value * 1000) / 10 }%`,
    value
  }));

export class CompanyDividendStatement extends EntityChangeData {
  static override readonly $type = 'CompanyDividendStatement';
  declarationDate: Date;
  paymentDate: Date;
  dividendType: DividendType;
  paymentType: PaymentType;
  equalDividendsForAllShareClasses: boolean;
  dividendsPerShareClassIfNotEqual: Record<string, number> = {};
  percentageOfDividendsFrankedIfNotEqual: Record<string, number> = {};
  percentageOfDividendsFranked: number;
  amountOfDistribution: number;
  companyTaxApplied: number;

  constructor(data: Partial<CompanyDividendStatement> = {}) {
    super(data);

    this.declarationDate = data.declarationDate ?? new Date();
    this.paymentDate = data.paymentDate ?? new Date();
    this.dividendType = data.dividendType ?? DividendType.Final;
    this.paymentType = data.paymentType ?? PaymentType.Cash;
    this.equalDividendsForAllShareClasses = data.equalDividendsForAllShareClasses ?? false;
    this.dividendsPerShareClassIfNotEqual = data.dividendsPerShareClassIfNotEqual ?? {};
    this.percentageOfDividendsFrankedIfNotEqual = data.percentageOfDividendsFrankedIfNotEqual ?? {};
    this.percentageOfDividendsFranked = data.percentageOfDividendsFranked ?? 0;
    this.amountOfDistribution = data.amountOfDistribution ?? 0;
    this.companyTaxApplied = data.companyTaxApplied ?? 0;
  }

  toDictionary(): { key: string; value: string; }[] {
    const dict = new ChangeDictionaryHelper();

    dict
      .addDate('Date of Declaration', this.declarationDate)
      .addDate('Proposed Payment Date', this.paymentDate)
      .add('Payment Type', PaymentType[this.paymentType])
      .add('Dividend Type', DividendType[this.dividendType])
      .addYesNo('Are dividends split equally between all share classes?', this.equalDividendsForAllShareClasses)
      .add('What company tax rate to be applied for franking purposes?', this.companyTaxApplied);

    if (this.equalDividendsForAllShareClasses) {
      dict
        .add('Amount of Distribution', this.amountOfDistribution)
        .add('Percentage of the distribution is franked', this.percentageOfDividendsFranked);
    } else {
      Object.entries(this.dividendsPerShareClassIfNotEqual)
        .forEach(([shareClass, amount]) => {
          dict
            .addDivider()
            .add(`Amount of Distribution for class ${ shareClass }`, amount)
            .add('Percentage of the distribution is franked', this.percentageOfDividendsFrankedIfNotEqual[shareClass]);
        });
    }

    return dict.dictionary;
  }

  fillModal(modalRef: NgbModalRef, document: Document, actualCompany: Company): NgbModalRef {
    modalRef = this.fillModalWithCompany(modalRef, actualCompany, true);
    (modalRef.componentInstance as CompanyDividendStatementComponent).companyChangeData.documentId = document.documentId;
    return modalRef;
  }

  fillModalWithCompany(modalRef: NgbModalRef, company: Company, isEdit: boolean): NgbModalRef {
    const instance = modalRef.componentInstance as CompanyDividendStatementComponent;

    instance.isEdit = isEdit;
    instance.companyChangeData = new CompanyChangeData(company);
    instance.formModel = structuredClone(this);

    return modalRef;
  }

  override prepareToRequest(): CompanyDividendStatement {
    return {
      ...this,
      ...super.prepareToRequest(),
      declarationDate: DatepickerHelper.buildDateString(this.declarationDate),
      paymentDate: DatepickerHelper.buildDateString(this.paymentDate),
    };
  }
}
